import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Trail, isMantrailingTrail, isHikingTrail, isDetectionTrail, isCanicrossTrail } from "../../types/trail";
import { 
  TrendingUp, 
  Award, 
  Calendar,
  Zap,
  Timer,
  Ruler,
  Target,
  BarChart3
} from "lucide-react";
import { 
  BarChart, 
  Bar, 
  LineChart, 
  Line, 
  PieChart, 
  Pie, 
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from "recharts";
import TrailIcon from "../trail/TrailIcon";
import HikeIcon from "../icon/HikeIcon";
import DetectionIcon from "../icon/DetectionIcon";
import CanicrossIcon from "../icon/CanicrossIcon";
import { Button } from "../ui/button";
import { formatDuration } from "../../utils/utils";
import { getStatisticsOverview } from "../../utils/api";
import { StatisticsActivityPoint, StatisticsOverview, StatisticsRecord, StatisticsSpeedRecords } from "../../types/overview";

export function StatisticsPage() {
  const [statistics, setStatistics] = useState<StatisticsOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    getStatisticsOverview()
      .then((data) => {
        if (!cancelled) setStatistics(data);
      })
      .catch((requestError) => {
        if (!cancelled) setError(requestError instanceof Error ? requestError.message : "Impossible de charger les statistiques.");
      });
    return () => { cancelled = true; };
  }, [retryKey]);

  if (error) {
    return (
      <div className="p-6 text-center" role="alert">
        <p>Impossible de charger les statistiques. {error}</p>
        <Button className="mt-3" onClick={() => setRetryKey((key) => key + 1)}>Réessayer</Button>
      </div>
    );
  }
  if (!statistics) return <div className="p-6">Chargement des statistiques...</div>;
  return <StatisticsContent statistics={statistics} />;
}

type SpeedMode = "speed" | "pace";

const toKmh = (metersPerSecond: number) => metersPerSecond * 3.6;
const toPaceMinutes = (metersPerSecond: number) => (metersPerSecond > 0 ? 1000 / metersPerSecond / 60 : 0);

const formatPaceMinutes = (minutesPerKm: number) => {
  if (!minutesPerKm || minutesPerKm <= 0) return "–";
  let minutes = Math.floor(minutesPerKm);
  let seconds = Math.round((minutesPerKm - minutes) * 60);
  if (seconds === 60) {
    minutes += 1;
    seconds = 0;
  }
  return `${minutes}'${String(seconds).padStart(2, "0")}"`;
};

const formatSpeedValue = (metersPerSecond: number, mode: SpeedMode) => (
  mode === "speed" ? toKmh(metersPerSecond).toFixed(1) : formatPaceMinutes(toPaceMinutes(metersPerSecond))
);

function SpeedCards({ stats, available, mode }: { stats: StatisticsSpeedRecords; available: boolean; mode: SpeedMode }) {
  if (!available) {
    return (
      <div className="text-center p-6 bg-gray-50 rounded-lg">
        <p className="text-muted-foreground">Données non disponibles pour calculer la vitesse.</p>
      </div>
    );
  }
  const unit = mode === "speed" ? "km/h" : "min/km";
  const isSpeed = mode === "speed";
  const dateOf = (trail: StatisticsRecord | null) => trail ? new Date(trail.date).toLocaleDateString('fr-FR') : null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-lg border-2 border-orange-200">
        <div className="flex items-center gap-2 mb-3"><Zap className="h-5 w-5 text-orange-600" /><h3 className="text-orange-900">{isSpeed ? "Vitesse max" : "Allure la plus rapide"}</h3></div>
        <p className="text-3xl text-orange-900 mb-2">{formatSpeedValue(stats.maxSpeed, mode)}</p>
        <p className="text-sm text-muted-foreground mb-2">{unit}</p>
        {stats.fastestTrail && <Badge className="bg-orange-600">{dateOf(stats.fastestTrail)}</Badge>}
      </div>
      <div className="bg-gradient-to-br from-cyan-50 to-teal-50 p-6 rounded-lg border-2 border-cyan-200">
        <div className="flex items-center gap-2 mb-3"><Target className="h-5 w-5 text-cyan-600" /><h3 className="text-cyan-900">{isSpeed ? "Vitesse min" : "Allure la plus lente"}</h3></div>
        <p className="text-3xl text-cyan-900 mb-2">{formatSpeedValue(stats.minSpeed, mode)}</p>
        <p className="text-sm text-muted-foreground mb-2">{unit}</p>
        {stats.slowestTrail && <Badge className="bg-cyan-600">{dateOf(stats.slowestTrail)}</Badge>}
      </div>
      <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-lg border-2 border-indigo-200">
        <div className="flex items-center gap-2 mb-3"><TrendingUp className="h-5 w-5 text-indigo-600" /><h3 className="text-indigo-900">{isSpeed ? "Vitesse moyenne" : "Allure moyenne"}</h3></div>
        <p className="text-3xl text-indigo-900 mb-2">{formatSpeedValue(stats.avgSpeed, mode)}</p>
        <p className="text-sm text-muted-foreground">{unit}</p>
      </div>
    </div>
  );
}

const toTrail = (activity: StatisticsActivityPoint): Trail => ({
  ...activity,
  ...(activity.category === "mantrailing" || activity.category === "detection"
    ? { dog: { name: activity.dogName } }
    : {}),
  ...(activity.category === "canicross"
    ? { dogId: { name: activity.dogName } }
    : {}),
} as unknown as Trail);

const toRecordTrail = (record: StatisticsRecord | null): Trail | undefined => (
  record ? toTrail(record as StatisticsActivityPoint) : undefined
);

function StatisticsContent({ statistics }: { statistics: StatisticsOverview }) {
  const trails = statistics.activities.map(toTrail);
  // Calculate global statistics
  const totalTrails = statistics.totalActivityCount;
  const totalDistance = statistics.totalDistance;
  const totalDuration = statistics.totalDuration;
  
  // Calculate category statistics
  const mantrailingTrails = trails.filter(isMantrailingTrail);
  const hikingTrails = trails.filter(isHikingTrail);
  const detectionTrails = trails.filter(isDetectionTrail);
  const canicrossTrails = trails.filter(isCanicrossTrail);
  
  const mantrailingDistance = statistics.categories.mantrailing.distance;
  const hikingDistance = statistics.categories.hiking.distance;
  const mantrailingDuration = statistics.categories.mantrailing.duration;
  const hikingDuration = statistics.categories.hiking.duration;
  const detectionDuration = statistics.categories.detection.duration;
  const canicrossDistance = statistics.categories.canicross.distance;
  const canicrossDuration = statistics.categories.canicross.duration;


  // --- Records Calculations ---

  // Distance Records
  const longestMantrailingTrail = toRecordTrail(statistics.records.distance.mantrailing.longest);
  const shortestMantrailingTrail = toRecordTrail(statistics.records.distance.mantrailing.shortest);
  const longestHikingTrail = toRecordTrail(statistics.records.distance.hiking.longest);
  const shortestHikingTrail = toRecordTrail(statistics.records.distance.hiking.shortest);

  // Duration Records
  const longestMantrailingDuration = toRecordTrail(statistics.records.duration.mantrailing.longest);
  const shortestMantrailingDuration = toRecordTrail(statistics.records.duration.mantrailing.shortest);
  const longestHikingDuration = toRecordTrail(statistics.records.duration.hiking.longest);
  const shortestHikingDuration = toRecordTrail(statistics.records.duration.hiking.shortest);
  const longestDetectionDuration = toRecordTrail(statistics.records.duration.detection.longest);
  const shortestDetectionDuration = toRecordTrail(statistics.records.duration.detection.shortest);
  const longestCanicrossTrail = statistics.records.distance.canicross.longest;
  const shortestCanicrossTrail = statistics.records.distance.canicross.shortest;
  const longestCanicrossDuration = statistics.records.duration.canicross.longest;
  const shortestCanicrossDuration = statistics.records.duration.canicross.shortest;
  const canicrossSpeedStats = statistics.records.speed.canicross;

  const mantrailingSpeedStats = statistics.records.speed.mantrailing;
  const hikingSpeedStats = statistics.records.speed.hiking;
  const monthlyChartData = statistics.monthly;

  const [distanceFilter, setDistanceFilter] = useState<"all" | "mantrailing" | "hiking" | "canicross">("all");

  // Distance breakdown over time
  const distanceOverTimeData = trails
    .filter(trail => {
      if ((trail.distance ?? 0) <= 0) return false;
      if (distanceFilter === 'all') return true;
      return trail.category === distanceFilter;
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map(trail => ({
      date: new Date(trail.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
      distance: trail.distance,
      mantrailingDistance: isMantrailingTrail(trail) && (distanceFilter === 'all' || distanceFilter === 'mantrailing') ? trail.distance : undefined,
      hikingDistance: isHikingTrail(trail) && (distanceFilter === 'all' || distanceFilter === 'hiking') ? trail.distance : undefined,
      canicrossDistance: isCanicrossTrail(trail) && (distanceFilter === 'all' || distanceFilter === 'canicross') ? trail.distance : undefined,
    }));

  const [speedMode, setSpeedMode] = useState<SpeedMode>("speed");
  const [speedFilter, setSpeedFilter] = useState<"all" | "mantrailing" | "hiking" | "canicross">("all");

  const speedOverTimeData = trails
    .filter((trail) => (trail.distance ?? 0) > 0 && (trail.duration ?? 0) > 0 && trail.category !== "detection")
    .filter((trail) => speedFilter === "all" || trail.category === speedFilter)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map((trail) => {
      const metersPerSecond = (trail.distance as number) / (trail.duration as number);
      return {
        date: new Date(trail.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        [trail.category]: speedMode === 'speed' ? toKmh(metersPerSecond) : toPaceMinutes(metersPerSecond),
      };
    });

  const [durationFilter, setDurationFilter] = useState<"all" | "mantrailing" | "hiking" | "detection" | "canicross">("all");

  const durationOverTimeData = trails
    .filter(trail => {
      if ((trail.duration ?? 0) <= 0) return false;
      if (durationFilter === 'all') return true;
      return trail.category === durationFilter;
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map(trail => ({
      date: new Date(trail.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
      duration: trail.duration,
      mantrailingDuration: isMantrailingTrail(trail) && (durationFilter === 'all' || durationFilter === 'mantrailing') ? trail.duration : undefined,
      hikingDuration: isHikingTrail(trail) && (durationFilter === 'all' || durationFilter === 'hiking') ? trail.duration : undefined,
      detectionDuration: isDetectionTrail(trail) && (durationFilter === 'all' || durationFilter === 'detection') ? trail.duration : undefined,
      canicrossDuration: isCanicrossTrail(trail) && (durationFilter === 'all' || durationFilter === 'canicross') ? trail.duration : undefined,
    }));


  // Category pie chart data
  const categoryData = [
    { name: "Mantrailing", value: statistics.categories.mantrailing.count, color: "#3b82f6" },
    { name: "Randonnée", value: statistics.categories.hiking.count, color: "#22c55e" },
    { name: "Détection", value: statistics.categories.detection.count, color: "#8b5cf6" },
    { name: "Canicross", value: statistics.categories.canicross.count, color: "#f97316" },
  ].filter((category) => category.value > 0);

  const hasMantrailing = statistics.categories.mantrailing.count > 0;
  const hasHiking = statistics.categories.hiking.count > 0;
  const hasDetection = statistics.categories.detection.count > 0;
  const hasCanicross = statistics.categories.canicross.count > 0;
  const hasSpeedRecords = hasMantrailing || hasHiking || hasCanicross;

 

  return (
    <div className="statistics-page h-full w-full overflow-auto bg-gradient-to-br from-blue-50 via-white to-purple-50">
    <div className="statistics-page__content max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
    <div className="statistics-page__heading flex items-center gap-3">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-3 rounded-lg">
            <BarChart3 className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl text-blue-900">Statistiques</h1>
            <p className="text-muted-foreground">Analyse de vos performances et progrès</p>
          </div>
        </div>

        {/* Global Statistics */}
        <Card className="statistics-page__summary border-2 border-blue-200 shadow-xl">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-blue-600" />
              Statistiques Globales
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="statistics-summary-grid grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-lg shadow-lg">
                <div className="flex justify-center mb-2">
                  <Award className="h-8 w-8 opacity-80" />
                </div>
                <p className="text-sm opacity-90 mb-1">Nombre de pistes</p>
                <p className="text-4xl mb-1">{totalTrails}</p>
                <div className="flex justify-center flex-wrap gap-2 text-xs opacity-90">
                  {hasMantrailing && <span className="flex items-center gap-1">
                    <TrailIcon className="h-3 w-3" /> {mantrailingTrails.length}
                  </span>}
                  {hasHiking && <span className="flex items-center gap-1">
                    <HikeIcon className="h-3 w-3" /> {hikingTrails.length}
                  </span>}
                  {hasDetection && <span className="flex items-center gap-1">
                    <DetectionIcon className="h-3 w-3" /> {detectionTrails.length}
                  </span>}
                  {hasCanicross && <span className="flex items-center gap-1">
                    <CanicrossIcon className="h-3 w-3" /> {canicrossTrails.length}
                  </span>}
                </div>
              </div>

              <div className="text-center p-6 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-lg shadow-lg">
                <div className="flex justify-center mb-2">
                  <Ruler className="h-8 w-8 opacity-80" />
                </div>
                <p className="text-sm opacity-90 mb-1">Distance totale</p>
                <p className="text-4xl mb-1">{(totalDistance / 1000).toFixed(2)}</p>
                <p className="text-sm opacity-90">km</p>
              </div>

              <div className="text-center p-6 bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-lg shadow-lg">
                <div className="flex justify-center mb-2">
                  <Timer className="h-8 w-8 opacity-80" />
                </div>
                <p className="text-sm opacity-90 mb-1">Durée totale</p>
                <p className="text-2xl mb-1">{formatDuration(totalDuration)}</p>
                <p className="text-xs opacity-90">d'activité</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="statistics-panels-grid grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Category Breakdown */}
          <Card className="shadow-lg border-blue-100">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-green-50">
              <CardTitle className="flex items-center gap-2">
                Répartition par activité
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="statistics-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="statistics-category-grid grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                {hasMantrailing && <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <TrailIcon className="h-4 w-4 text-blue-600" />
                    <span className="text-sm">Mantrailing</span>
                  </div>
                  <p className="text-2xl text-blue-900">{mantrailingTrails.length}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {(mantrailingDistance / 1000).toFixed(2)} km
                  </p>
                </div>}
                {hasHiking && <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <HikeIcon className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Randonnée</span>
                  </div>
                  <p className="text-2xl text-green-900">{hikingTrails.length}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {(hikingDistance / 1000).toFixed(2)} km
                  </p>
                </div>}
                {hasDetection && <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                        <DetectionIcon className="h-4 w-4 text-purple-600" />
                        <span className="text-sm">Détection</span>
                    </div>
                    <p className="text-2xl text-purple-900">{detectionTrails.length}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                        {formatDuration(detectionDuration)}
                    </p>
                </div>}
                {hasCanicross && <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <CanicrossIcon className="h-4 w-4 text-orange-600" />
                    <span className="text-sm">Canicross</span>
                  </div>
                  <p className="text-2xl text-orange-900">{canicrossTrails.length}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {(canicrossDistance / 1000).toFixed(2)} km · {formatDuration(canicrossDuration)}
                  </p>
                </div>}
              </div>
            </CardContent>
          </Card>

          {/* Monthly Trends */}
          <Card className="shadow-lg border-purple-100">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-purple-600" />
                Activité mensuelle
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="statistics-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyChartData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    {hasMantrailing && <Bar dataKey="mantrailing" name="Mantrailing" fill="#3b82f6" />}
                    {hasHiking && <Bar dataKey="hiking" name="Randonnée" fill="#22c55e" />}
                    {hasDetection && <Bar dataKey="detection" name="Détection" fill="#8b5cf6" />}
                    {hasCanicross && <Bar dataKey="canicross" name="Canicross" fill="#f97316" />}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Records */}
        <Card className="shadow-lg border-orange-100">
          <CardHeader className="bg-gradient-to-r from-orange-50 to-yellow-50">
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-orange-600" />
              Records personnels
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <Tabs defaultValue="distance" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="distance">Distance</TabsTrigger>
                <TabsTrigger value="duration">Durée</TabsTrigger>
                <TabsTrigger value="speed">Vitesse</TabsTrigger>
              </TabsList>
              
              <TabsContent value="distance" className="space-y-4 mt-4">
                <div className="space-y-6">
                  {/* Mantrailing Distance */}
                  {hasMantrailing && <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-blue-800"><TrailIcon className="h-5 w-5" />Mantrailing</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-blue-50 to-sky-50 p-6 rounded-lg border-2 border-blue-200">
                        <div className="flex items-center gap-2 mb-3"><TrendingUp className="h-5 w-5 text-blue-600" /><h3 className="text-blue-900">Plus longue piste</h3></div>
                        <p className="text-3xl text-blue-900 mb-2">{(longestMantrailingTrail?.distance ?? 0)} m</p>
                        {longestMantrailingTrail && isMantrailingTrail(longestMantrailingTrail) && <><p className="text-sm text-muted-foreground mb-1">{`${longestMantrailingTrail.dog.name} - ${longestMantrailingTrail.location || ''}`}</p><Badge className="bg-blue-600">{new Date(longestMantrailingTrail.date).toLocaleDateString('fr-FR')}</Badge></>}
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-sky-50 p-6 rounded-lg border-2 border-blue-200">
                        <div className="flex items-center gap-2 mb-3"><Ruler className="h-5 w-5 text-blue-600" /><h3 className="text-blue-900">Plus courte piste</h3></div>
                        <p className="text-3xl text-blue-900 mb-2">{(shortestMantrailingTrail?.distance ?? 0)} m</p>                        
                        {shortestMantrailingTrail && isMantrailingTrail(shortestMantrailingTrail) && <><p className="text-sm text-muted-foreground mb-1">{`${shortestMantrailingTrail.dog.name} - ${shortestMantrailingTrail.location || ''}`}</p><Badge className="bg-blue-600">{new Date(shortestMantrailingTrail.date).toLocaleDateString('fr-FR')}</Badge></>}
                      </div>
                    </div>
                  </div>}
                  {/* Hiking Distance */}
                  {hasHiking && <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-green-800"><HikeIcon className="h-5 w-5" />Randonnée</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-lg border-2 border-green-200">
                        <div className="flex items-center gap-2 mb-3"><TrendingUp className="h-5 w-5 text-green-600" /><h3 className="text-green-900">Plus longue rando</h3></div>
                        <p className="text-3xl text-green-900 mb-2">{(longestHikingTrail?.distance ?? 0)} m</p>
                        {longestHikingTrail && isHikingTrail(longestHikingTrail) && <><p className="text-sm text-muted-foreground mb-1">{longestHikingTrail.name}</p><Badge className="bg-green-600">{new Date(longestHikingTrail.date).toLocaleDateString('fr-FR')}</Badge></>}
                      </div>
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-lg border-2 border-green-200">
                        <div className="flex items-center gap-2 mb-3"><Ruler className="h-5 w-5 text-green-600" /><h3 className="text-green-900">Plus courte rando</h3></div>
                        <p className="text-3xl text-green-900 mb-2">{(shortestHikingTrail?.distance ?? 0)} m</p>                        
                        {shortestHikingTrail && isHikingTrail(shortestHikingTrail) && <><p className="text-sm text-muted-foreground mb-1">{shortestHikingTrail.name}</p><Badge className="bg-green-600">{new Date(shortestHikingTrail.date).toLocaleDateString('fr-FR')}</Badge></>}
                      </div>
                    </div>
                                          </div>}
                                          {/* Canicross Distance */}
                                          {hasCanicross && <div>
                                            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-orange-800"><CanicrossIcon className="h-5 w-5" />Canicross</h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                              <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-lg border-2 border-orange-200">
                                                <div className="flex items-center gap-2 mb-3"><TrendingUp className="h-5 w-5 text-orange-600" /><h3 className="text-orange-900">Plus longue course</h3></div>
                                                <p className="text-3xl text-orange-900 mb-2">{(longestCanicrossTrail?.distance ?? 0)} m</p>
                                                {longestCanicrossTrail && <><p className="text-sm text-muted-foreground mb-1">{longestCanicrossTrail.dogName}</p><Badge className="bg-orange-600">{new Date(longestCanicrossTrail.date).toLocaleDateString('fr-FR')}</Badge></>}
                                              </div>
                                              <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-lg border-2 border-orange-200">
                                                <div className="flex items-center gap-2 mb-3"><Ruler className="h-5 w-5 text-orange-600" /><h3 className="text-orange-900">Plus courte course</h3></div>
                                                <p className="text-3xl text-orange-900 mb-2">{(shortestCanicrossTrail?.distance ?? 0)} m</p>
                                                {shortestCanicrossTrail && <><p className="text-sm text-muted-foreground mb-1">{shortestCanicrossTrail.dogName}</p><Badge className="bg-orange-600">{new Date(shortestCanicrossTrail.date).toLocaleDateString('fr-FR')}</Badge></>}
                                              </div>
                                            </div>
                                          </div>}
                                        </div>
              </TabsContent>

              <TabsContent value="duration" className="space-y-4 mt-4">
                <div className="space-y-6">
                  {/* Mantrailing Duration */}
                  {hasMantrailing && <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-blue-800"><TrailIcon className="h-5 w-5" />Mantrailing</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-6 rounded-lg border-2 border-purple-200">
                        <div className="flex items-center gap-2 mb-3"><Timer className="h-5 w-5 text-purple-600" /><h3 className="text-purple-900">Durée la plus longue</h3></div>
                        {longestMantrailingDuration && isMantrailingTrail(longestMantrailingDuration) ? (<>
                          <p className="text-2xl text-purple-900 mb-2">{formatDuration(longestMantrailingDuration.duration ?? 0)}</p>
                          <p className="text-sm text-muted-foreground mb-1">{`${longestMantrailingDuration.dog.name} - ${longestMantrailingDuration.location || ''}`}</p>
                          <Badge className="bg-purple-600">{new Date(longestMantrailingDuration.date).toLocaleDateString('fr-FR')}</Badge>
                        </>) : <p className="text-2xl text-purple-900 mb-2">N/A</p>}
                      </div>
                      <div className="bg-gradient-to-br from-pink-50 to-rose-50 p-6 rounded-lg border-2 border-pink-200">
                        <div className="flex items-center gap-2 mb-3"><Zap className="h-5 w-5 text-pink-600" /><h3 className="text-pink-900">Durée la plus courte</h3></div>
                        {shortestMantrailingDuration && isMantrailingTrail(shortestMantrailingDuration) ? (<>
                          <p className="text-2xl text-pink-900 mb-2">{formatDuration(shortestMantrailingDuration.duration ?? 0)}</p>
                          <p className="text-sm text-muted-foreground mb-1">{`${shortestMantrailingDuration.dog.name} - ${shortestMantrailingDuration.location || ''}`}</p>
                          <Badge className="bg-pink-600">{new Date(shortestMantrailingDuration.date).toLocaleDateString('fr-FR')}</Badge>
                        </>) : <p className="text-2xl text-pink-900 mb-2">N/A</p>}
                      </div>
                    </div>
                  </div>}
                  {/* Hiking Duration */}
                  {hasHiking && <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-green-800"><HikeIcon className="h-5 w-5" />Randonnée</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-6 rounded-lg border-2 border-purple-200">
                        <div className="flex items-center gap-2 mb-3"><Timer className="h-5 w-5 text-purple-600" /><h3 className="text-purple-900">Durée la plus longue</h3></div>
                        {longestHikingDuration && isHikingTrail(longestHikingDuration) ? (<>
                          <p className="text-2xl text-purple-900 mb-2">{formatDuration(longestHikingDuration.duration ?? 0)}</p>
                          <p className="text-sm text-muted-foreground mb-1">{longestHikingDuration.name}</p>
                          <Badge className="bg-purple-600">{new Date(longestHikingDuration.date).toLocaleDateString('fr-FR')}</Badge>
                        </>) : <p className="text-2xl text-purple-900 mb-2">N/A</p>}
                      </div>
                      <div className="bg-gradient-to-br from-pink-50 to-rose-50 p-6 rounded-lg border-2 border-pink-200">
                        <div className="flex items-center gap-2 mb-3"><Zap className="h-5 w-5 text-pink-600" /><h3 className="text-pink-900">Durée la plus courte</h3></div>
                        {shortestHikingDuration && isHikingTrail(shortestHikingDuration) ? (<>
                          <p className="text-2xl text-pink-900 mb-2">{formatDuration(shortestHikingDuration.duration ?? 0)}</p>
                          <p className="text-sm text-muted-foreground mb-1">{shortestHikingDuration.name}</p>
                          <Badge className="bg-pink-600">{new Date(shortestHikingDuration.date).toLocaleDateString('fr-FR')}</Badge>
                        </>) : <p className="text-2xl text-pink-900 mb-2">N/A</p>}
                      </div>
                    </div>
                  </div>}
                  {/* Detection Duration */}
                  {hasDetection && <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-purple-800"><DetectionIcon className="h-5 w-5" />Détection</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-6 rounded-lg border-2 border-purple-200">
                        <div className="flex items-center gap-2 mb-3"><Timer className="h-5 w-5 text-purple-600" /><h3 className="text-purple-900">Durée la plus longue</h3></div>
                        {longestDetectionDuration && isDetectionTrail(longestDetectionDuration) ? (<>
                          <p className="text-2xl text-purple-900 mb-2">{formatDuration(longestDetectionDuration.duration ?? 0)}</p>
                          <p className="text-sm text-muted-foreground mb-1">{`${longestDetectionDuration.dog.name} - ${longestDetectionDuration.location || ''}`}</p>
                          <Badge className="bg-purple-600">{new Date(longestDetectionDuration.date).toLocaleDateString('fr-FR')}</Badge>
                        </>) : <p className="text-2xl text-purple-900 mb-2">N/A</p>}
                      </div>
                      <div className="bg-gradient-to-br from-pink-50 to-rose-50 p-6 rounded-lg border-2 border-pink-200">
                        <div className="flex items-center gap-2 mb-3"><Zap className="h-5 w-5 text-pink-600" /><h3 className="text-pink-900">Durée la plus courte</h3></div>
                        {shortestDetectionDuration && isDetectionTrail(shortestDetectionDuration) ? (<>
                          <p className="text-2xl text-pink-900 mb-2">{formatDuration(shortestDetectionDuration.duration ?? 0)}</p>
                          <p className="text-sm text-muted-foreground mb-1">{`${shortestDetectionDuration.dog.name} - ${shortestDetectionDuration.location || ''}`}</p>
                          <Badge className="bg-pink-600">{new Date(shortestDetectionDuration.date).toLocaleDateString('fr-FR')}</Badge>
                        </>) : <p className="text-2xl text-pink-900 mb-2">N/A</p>}
                      </div>
                    </div>
                  </div>}
                  {/* Canicross Duration */}
                  {hasCanicross && <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-orange-800"><CanicrossIcon className="h-5 w-5" />Canicross</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-6 rounded-lg border-2 border-purple-200">
                        <div className="flex items-center gap-2 mb-3"><Timer className="h-5 w-5 text-purple-600" /><h3 className="text-purple-900">Durée la plus longue</h3></div>
                        {longestCanicrossDuration ? (<>
                          <p className="text-2xl text-purple-900 mb-2">{formatDuration(longestCanicrossDuration.duration ?? 0)}</p>
                          <p className="text-sm text-muted-foreground mb-1">{longestCanicrossDuration.dogName}</p>
                          <Badge className="bg-purple-600">{new Date(longestCanicrossDuration.date).toLocaleDateString('fr-FR')}</Badge>
                        </>) : <p className="text-2xl text-purple-900 mb-2">N/A</p>}
                      </div>
                      <div className="bg-gradient-to-br from-pink-50 to-rose-50 p-6 rounded-lg border-2 border-pink-200">
                        <div className="flex items-center gap-2 mb-3"><Zap className="h-5 w-5 text-pink-600" /><h3 className="text-pink-900">Durée la plus courte</h3></div>
                        {shortestCanicrossDuration ? (<>
                          <p className="text-2xl text-pink-900 mb-2">{formatDuration(shortestCanicrossDuration.duration ?? 0)}</p>
                          <p className="text-sm text-muted-foreground mb-1">{shortestCanicrossDuration.dogName}</p>
                          <Badge className="bg-pink-600">{new Date(shortestCanicrossDuration.date).toLocaleDateString('fr-FR')}</Badge>
                        </>) : <p className="text-2xl text-pink-900 mb-2">N/A</p>}
                      </div>
                    </div>
                  </div>}
                </div>
              </TabsContent>

              <TabsContent value="speed" className="space-y-4 mt-4">
                <div className="flex justify-end">
                  <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg" role="group" aria-label="Affichage de la vitesse">
                    <Button size="sm" variant={speedMode === 'speed' ? 'secondary' : 'ghost'} onClick={() => setSpeedMode('speed')} className="h-7">Vitesse (km/h)</Button>
                    <Button size="sm" variant={speedMode === 'pace' ? 'secondary' : 'ghost'} onClick={() => setSpeedMode('pace')} className="h-7">Allure (min/km)</Button>
                  </div>
                </div>
                <div className="space-y-6">
                  {/* Mantrailing Speed */}
                  {hasMantrailing && <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-blue-800"><TrailIcon className="h-5 w-5" />Mantrailing</h3>
                    <SpeedCards stats={mantrailingSpeedStats} available={mantrailingDuration > 0} mode={speedMode} />
                  </div>}

                  {/* Hiking Speed */}
                  {hasHiking && <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-green-800"><HikeIcon className="h-5 w-5" />Randonnée</h3>
                    <SpeedCards stats={hikingSpeedStats} available={hikingDuration > 0} mode={speedMode} />
                  </div>}

                  {/* Canicross Speed */}
                  {hasCanicross && <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-orange-800"><CanicrossIcon className="h-5 w-5" />Canicross</h3>
                    <SpeedCards stats={canicrossSpeedStats} available={canicrossDuration > 0} mode={speedMode} />
                  </div>}

                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Progress Over Time */}
        <Card className="shadow-lg border-green-100">
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
              <div className="statistics-filter-heading flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                Évolution des distances
              </CardTitle>
              <div className="statistics-filter-controls flex items-center gap-1 bg-white/50 p-1 rounded-lg">
                <Button size="sm" variant={distanceFilter === 'all' ? 'secondary' : 'ghost'} onClick={() => setDistanceFilter('all')} className="h-7">
                  Tout
                </Button>
                {hasMantrailing && <Button size="sm" variant={distanceFilter === 'mantrailing' ? 'secondary' : 'ghost'} onClick={() => setDistanceFilter('mantrailing')} className="h-7">
                  <TrailIcon className="h-4 w-4 mr-1" />
                  Mantrailing
                </Button>}
                {hasHiking && <Button size="sm" variant={distanceFilter === 'hiking' ? 'secondary' : 'ghost'} onClick={() => setDistanceFilter('hiking')} className="h-7">
                  <HikeIcon className="h-4 w-4 mr-1" />
                  Randonnée
                </Button>}
                {hasCanicross && <Button size="sm" variant={distanceFilter === 'canicross' ? 'secondary' : 'ghost'} onClick={() => setDistanceFilter('canicross')} className="h-7">
                  <CanicrossIcon className="h-4 w-4 mr-1" />
                  Canicross
                </Button>}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="statistics-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={distanceOverTimeData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="date" />
                  <YAxis label={{ value: 'Distance (m)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  {(distanceFilter === 'all' || distanceFilter === 'mantrailing') && <Line 
                    type="monotone" 
                    dataKey="mantrailingDistance"
                    name="Mantrailing"
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', r: 4 }}
                    activeDot={{ r: 6 }}
                    connectNulls
                  />}
                  {(distanceFilter === 'all' || distanceFilter === 'hiking') && <Line
                    type="monotone" 
                    dataKey="hikingDistance"
                    name="Randonnée"
                    stroke="#22c55e" 
                    strokeWidth={2}
                    dot={{ fill: '#22c55e', r: 4 }}
                    activeDot={{ r: 6 }}
                    connectNulls
                  />}
                  {(distanceFilter === 'all' || distanceFilter === 'canicross') && <Line
                    type="monotone" 
                    dataKey="canicrossDistance"
                    name="Canicross"
                    stroke="#f97316" 
                    strokeWidth={2}
                    dot={{ fill: '#f97316', r: 4 }}
                    activeDot={{ r: 6 }}
                    connectNulls
                  />}
                </LineChart>
              </ResponsiveContainer>
                    </div>
          </CardContent>
        </Card>

        {/* Duration Progress Over Time */}
        <Card className="shadow-lg border-purple-100">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="statistics-filter-heading flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Timer className="h-5 w-5 text-purple-600" />
                Évolution des durées
              </CardTitle>
              <div className="statistics-filter-controls flex items-center gap-1 bg-white/50 p-1 rounded-lg">
                <Button size="sm" variant={durationFilter === 'all' ? 'secondary' : 'ghost'} onClick={() => setDurationFilter('all')} className="h-7">
                  Tout
                </Button>
                {hasMantrailing && <Button size="sm" variant={durationFilter === 'mantrailing' ? 'secondary' : 'ghost'} onClick={() => setDurationFilter('mantrailing')} className="h-7">
                  <TrailIcon className="h-4 w-4 mr-1" />
                  Mantrailing
                </Button>}
                {hasHiking && <Button size="sm" variant={durationFilter === 'hiking' ? 'secondary' : 'ghost'} onClick={() => setDurationFilter('hiking')} className="h-7">
                  <HikeIcon className="h-4 w-4 mr-1" />
                  Randonnée
                </Button>}
                {hasDetection && <Button size="sm" variant={durationFilter === 'detection' ? 'secondary' : 'ghost'} onClick={() => setDurationFilter('detection')} className="h-7">
                  <DetectionIcon className="h-4 w-4 mr-1" />
                  Détection
                </Button>}
                {hasCanicross && <Button size="sm" variant={durationFilter === 'canicross' ? 'secondary' : 'ghost'} onClick={() => setDurationFilter('canicross')} className="h-7">
                  <CanicrossIcon className="h-4 w-4 mr-1" />
                  Canicross
                </Button>}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="statistics-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={durationOverTimeData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="date" />
                  <YAxis 
                    label={{ value: 'Durée (min)', angle: -90, position: 'insideLeft' }}
                    tickFormatter={(value) => Math.round(value / 60).toString()}
                  />
                  <Tooltip formatter={(value: number) => formatDuration(value)} />
                  <Legend />
                  {(durationFilter === 'all' || durationFilter === 'mantrailing') && <Line 
                    type="monotone" 
                    dataKey="mantrailingDuration"
                    name="Mantrailing"
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    dot={{ fill: '#3b82f6', r: 4 }}
                    activeDot={{ r: 6 }}
                    connectNulls
                  />}
                  {(durationFilter === 'all' || durationFilter === 'hiking') && <Line
                    type="monotone" 
                    dataKey="hikingDuration"
                    name="Randonnée"
                    stroke="#22c55e" 
                    strokeWidth={2}
                    dot={{ fill: '#22c55e', r: 4 }}
                    activeDot={{ r: 6 }}
                    connectNulls
                  />}
                  {(durationFilter === 'all' || durationFilter === 'detection') && <Line
                    type="monotone" 
                    dataKey="detectionDuration"
                    name="Détection"
                    stroke="#8b5cf6" 
                    strokeWidth={2}
                    dot={{ fill: '#8b5cf6', r: 4 }}
                    activeDot={{ r: 6 }}
                    connectNulls
                  />}
                  {(durationFilter === 'all' || durationFilter === 'canicross') && <Line
                    type="monotone" 
                    dataKey="canicrossDuration"
                    name="Canicross"
                    stroke="#f97316" 
                    strokeWidth={2}
                    dot={{ fill: '#f97316', r: 4 }}
                    activeDot={{ r: 6 }}
                    connectNulls
                  />}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Speed / Pace Progress Over Time */}
        {hasSpeedRecords && (
          <Card className="shadow-lg border-orange-100">
            <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50">
              <div className="statistics-filter-heading flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-orange-600" />
                  {speedMode === 'speed' ? 'Évolution des vitesses' : 'Évolution des allures'}
                </CardTitle>
                <div className="statistics-filter-controls flex items-center gap-1 bg-white/50 p-1 rounded-lg">
                  <Button size="sm" variant={speedFilter === 'all' ? 'secondary' : 'ghost'} onClick={() => setSpeedFilter('all')} className="h-7">
                    Tout
                  </Button>
                  {hasMantrailing && <Button size="sm" variant={speedFilter === 'mantrailing' ? 'secondary' : 'ghost'} onClick={() => setSpeedFilter('mantrailing')} className="h-7">
                    <TrailIcon className="h-4 w-4 mr-1" />
                    Mantrailing
                  </Button>}
                  {hasHiking && <Button size="sm" variant={speedFilter === 'hiking' ? 'secondary' : 'ghost'} onClick={() => setSpeedFilter('hiking')} className="h-7">
                    <HikeIcon className="h-4 w-4 mr-1" />
                    Randonnée
                  </Button>}
                  {hasCanicross && <Button size="sm" variant={speedFilter === 'canicross' ? 'secondary' : 'ghost'} onClick={() => setSpeedFilter('canicross')} className="h-7">
                    <CanicrossIcon className="h-4 w-4 mr-1" />
                    Canicross
                  </Button>}
                </div>
                <div className="statistics-filter-controls flex items-center gap-1 bg-white/50 p-1 rounded-lg">
                  <Button size="sm" variant={speedMode === 'speed' ? 'secondary' : 'ghost'} onClick={() => setSpeedMode('speed')} className="h-7">
                    Vitesse
                  </Button>
                  <Button size="sm" variant={speedMode === 'pace' ? 'secondary' : 'ghost'} onClick={() => setSpeedMode('pace')} className="h-7">
                    Allure
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="statistics-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={speedOverTimeData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="date" />
                    <YAxis
                      reversed={speedMode === 'pace'}
                      domain={['auto', 'auto']}
                      tickFormatter={(value) => speedMode === 'speed' ? Number(value).toFixed(1) : formatPaceMinutes(value)}
                      label={{ value: speedMode === 'speed' ? 'km/h' : 'min/km', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip formatter={(value: number) => `${speedMode === 'speed' ? value.toFixed(1) : formatPaceMinutes(value)} ${speedMode === 'speed' ? 'km/h' : 'min/km'}`} />
                    <Legend />
                    {hasMantrailing && (speedFilter === 'all' || speedFilter === 'mantrailing') && <Line type="monotone" dataKey="mantrailing" name="Mantrailing" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 4 }} activeDot={{ r: 6 }} connectNulls />}
                    {hasHiking && (speedFilter === 'all' || speedFilter === 'hiking') && <Line type="monotone" dataKey="hiking" name="Randonnée" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e', r: 4 }} activeDot={{ r: 6 }} connectNulls />}
                    {hasCanicross && (speedFilter === 'all' || speedFilter === 'canicross') && <Line type="monotone" dataKey="canicross" name="Canicross" stroke="#f97316" strokeWidth={2} dot={{ fill: '#f97316', r: 4 }} activeDot={{ r: 6 }} connectNulls />}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}